import React, { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import {
  useFragment as useApolloFragment,
  useMutation,
  useQuery,
} from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  List,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { DocumentNode } from 'graphql';
import {
  BikeIcon,
  HospitalIcon,
  MapPinIcon,
  MousePointerClickIcon,
  SchoolIcon,
  ShoppingCartIcon,
  SparklesIcon,
  TrainFrontIcon,
  UtensilsIcon,
} from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { GetManualPoisMutation } from '@graphql/__generated__/graphql';
import { ConfirmationDialog } from '@modules/_shared/components/ConfirmationDialog';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import {
  type ReportComponentProps,
  useReportContext,
} from '@modules/_shared/components/report-editor/ReportEditorContext';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { formatDistance } from '@modules/_shared/utils/formatting';
import { STEP_POIS_FRAGMENT } from '@modules/reports/cma-reports/cmaReportsQueries';
import { FooterActionsComponentWithoutForm } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import MapSettings from '@modules/reports/shared/StepPointsOfInterest/MapSettings';
import POIListItem from '@modules/reports/shared/StepPointsOfInterest/POIListItem';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { getBaseCmaEventProperties } from '@shared/analytics/posthog-helpers';
import { useLocale } from '@shared/hooks/locale';
import { cssTokens } from '@shared/theme/StyleProvider';

const GET_POI_TYPES = gql(/* GraphQL */ `
  query GetPoiTypes {
    dictionaries(
      where: { type: { _eq: poi_types } }
      order_by: { order_nr: asc_nulls_last }
    ) {
      id
      name
      label
      types
    }
  }
`);

const GET_AUTO_POIS = gql(/* GraphQL */ `
  mutation GetAutoPois($propertyId: uuid!) {
    auto_search_pois(property_id: $propertyId)
  }
`);

const GET_MANUAL_POIS = gql(/* GraphQL */ `
  mutation GetManualPois($propertyId: uuid!, $search: String!) {
    manual_search_pois(property_id: $propertyId, search: $search) {
      id
      name
      formattedAddress
      distance
      types
      category
      latitude
      longitude
      primaryType
    }
  }
`);

const INSERT_POI = gql(/* GraphQL */ `
  mutation InsertPoi($input: InsertPoisOneInput!) {
    insert_pois_one(input: $input)
  }
`);

type POICategory = {
  name: string;
  icon: React.ReactNode;
  primaryTypes: string[];
};

export type POIItem = {
  id: string;
  name: string;
  formattedAddress: string;
  distance: number;
  category: string; // e.g., "Shopping", "Transport"
  primaryType: string; // e.g., "supermarket", "bus_stop"
  types: string[]; // e.g., ["supermarket", "grocery_or_supermarket", ...]
  primaryMode: string;
  primaryDistanceMeters: number | null;
};

type StepPointsOfInterestProps = ReportComponentProps & {
  reportType: 'cma' | 'brochure';
  updateReport: () => Promise<void>;
  refetchQuery: DocumentNode;
  pageId: string;
  includePoiMap: boolean;
  poiMapZoomLevel: number | null;
  onIncludePoiMapChange: (value: boolean) => Promise<void>;
  onPoiMapZoomLevelChange: (value: number | null) => Promise<void>;
};

type ManualPoi = GetManualPoisMutation['manual_search_pois'][number];

const StepPointsOfInterest = (props: StepPointsOfInterestProps) => {
  const {
    updateReport,
    refetchQuery,
    includePoiMap,
    poiMapZoomLevel,
    onIncludePoiMapChange,
    onPoiMapZoomLevelChange,
  } = props;
  const { t } = useLocale();
  const { me } = useAppData();
  const { track } = useAnalytics();
  const { data: reportData } = useReportContext();

  const poiCategories: POICategory[] = [
    {
      name: t('Transport'),
      icon: <TrainFrontIcon color={'#3b82f6'} size={18} />,
      primaryTypes: ['Transport'],
    },
    {
      name: t('Shopping'),
      icon: <ShoppingCartIcon color={'#16a34a'} size={18} />,
      primaryTypes: ['Shopping'],
    },
    {
      name: t('Education'),
      icon: <SchoolIcon color={'#9333ea'} size={18} />,
      primaryTypes: ['Education'],
    },
    {
      name: t('Food & drink'),
      icon: <UtensilsIcon color={'#60a5fa'} size={18} />,
      primaryTypes: ['Food & Drink'],
    },
    {
      name: t('Sports & Leisure'),
      icon: <BikeIcon color={'#ca8a04'} size={18} />,
      primaryTypes: ['Sports & Leisure'],
    },
    {
      name: t('Healthcare'),
      icon: <HospitalIcon color={'#db2777'} size={18} />,
      primaryTypes: ['Healthcare'],
    },
    {
      name: t('Other'),
      icon: <MapPinIcon color={'#71717a'} size={18} />,
      primaryTypes: ['Other'],
    },
  ];

  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [manualSearchOpen, setManualSearchOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const { data: poiTypesData } = useQuery(GET_POI_TYPES);

  const property = reportData.property;
  const poisFragment = useApolloFragment({
    fragment: STEP_POIS_FRAGMENT,
    fragmentName: 'StepPois',
    from: { __typename: 'properties', id: property.id },
  });
  const propertyPois = poisFragment.complete
    ? poisFragment.data.property_pois
    : [];
  const reportId = reportData?.id;
  const propertyId = property?.id;
  const source =
    (reportData as { report_source?: string })?.report_source ?? null;
  const leadId = (reportData as { lead?: { id?: string } })?.lead?.id;

  const pois: POIItem[] = propertyPois.map(propertyPoi => {
    const poi = propertyPoi.poi;
    const straightLineDistance = propertyPoi.distance_meters;
    let primaryMode: string = 'driving';
    let primaryDistanceMeters: number | null = null;

    if (straightLineDistance != null) {
      let preferences: { mode: string; distance: number | null | undefined }[] =
        [];
      if (straightLineDistance < 1500) {
        preferences = [
          { mode: 'walking', distance: propertyPoi.walking_distance_meters },
          { mode: 'cycling', distance: propertyPoi.cycling_distance_meters },
          { mode: 'driving', distance: propertyPoi.distance_meters },
        ];
      } else if (straightLineDistance < 7000) {
        preferences = [
          { mode: 'cycling', distance: propertyPoi.cycling_distance_meters },
          { mode: 'driving', distance: propertyPoi.distance_meters },
          { mode: 'walking', distance: propertyPoi.walking_distance_meters },
        ];
      } else {
        preferences = [
          { mode: 'driving', distance: propertyPoi.distance_meters },
          { mode: 'cycling', distance: propertyPoi.cycling_distance_meters },
          { mode: 'walking', distance: propertyPoi.walking_distance_meters },
        ];
      }

      for (const pref of preferences) {
        if (pref.distance != null) {
          primaryMode = pref.mode;
          primaryDistanceMeters = pref.distance;
          break;
        }
      }
    }

    // If no distance was found in preferences, keep the default driving mode
    // and set the distance to the straight line distance
    if (primaryDistanceMeters == null && straightLineDistance != null) {
      primaryDistanceMeters = straightLineDistance;
    }

    return {
      id: propertyPoi.id,
      name: poi.name,
      formattedAddress: poi.formatted_address,
      category: poi.category,
      primaryType: poi.primary_type,
      types: poi.types || [],
      primaryMode,
      primaryDistanceMeters,
      distance: straightLineDistance,
    };
  });

  const [getManualPois, { data: searchData, loading: searchLoading }] =
    useMutation<GetManualPoisMutation>(GET_MANUAL_POIS);

  const [getPois, { loading: autoPoisLoading }] = useMutation(GET_AUTO_POIS);

  const [insertPoi] = useMutation(INSERT_POI);

  const handleSearchChange = (_event: React.SyntheticEvent, value: string) => {
    if (!value || !property?.id) {
      return;
    }

    getManualPois({
      variables: {
        propertyId: property.id,
        search: value,
      },
      onError: error => {
        setErrorLike(error);
      },
    });
  };

  const handlePlaceSelect = (
    place: GetManualPoisMutation['manual_search_pois'][number],
  ) => {
    if (!place) {
      return;
    }

    const googlePlaceId = place.id;

    insertPoi({
      variables: {
        input: {
          propertyId: property.id,
          placeId: googlePlaceId,
          name: place.name,
          formattedAddress: place.formattedAddress,
          types: place.types,
          latitude: place.latitude,
          longitude: place.longitude,
          distance: place.distance,
          primaryType: place.primaryType,
          category: place.category,
        },
      },
      onCompleted: () => {
        const baseProperties = getBaseCmaEventProperties(me, reportId);
        if (baseProperties) {
          track(ANALYTICS_EVENTS.CMA_POIS_MANUAL_SAVE, {
            ...baseProperties,
            source: source ?? 'unknown',
            ...(propertyId && { property_id: propertyId }),
            ...(leadId && { lead_id: leadId }),
          });
        }
        setManualSearchOpen(false);
        updateReport();
      },
      onError: error => {
        console.error('Error inserting POI:', error);
        setErrorLike(error);
      },
      refetchQueries: [{ query: refetchQuery, variables: { id: reportId } }],
    });
  };

  const handleAutoSearch = () => {
    const startTime = Date.now();

    getPois({
      variables: {
        propertyId: property.id,
      },
      onCompleted: () => {
        const baseProperties = getBaseCmaEventProperties(me, reportId);

        if (baseProperties) {
          track(ANALYTICS_EVENTS.CMA_POIS_AUTO_SEARCH, {
            ...baseProperties,
            source: source ?? 'unknown',
            search_duration_ms: Date.now() - startTime,
            ...(propertyId && { property_id: propertyId }),
            ...(leadId && { lead_id: leadId }),
          });
        }
        updateReport();
      },
      onError: setErrorLike,
      refetchQueries: [{ query: refetchQuery, variables: { id: reportId } }],
    });
  };

  const getPoiTypeLabel = (name: string) => {
    const poiType = poiTypesData?.dictionaries?.find(d => d.name === name);
    if (poiType?.label) {
      return poiType.label;
    }
    return name
      ? name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      : 'N/A';
  };

  const manualPoiOptions: ManualPoi[] = searchData?.manual_search_pois ?? [];

  if (!poisFragment.complete) {
    return (
      <Skeleton height={400} sx={{ my: 3, mx: 1 }} variant="rectangular" />
    );
  }

  return (
    <>
      <Stack
        sx={{
          gap: 1,
          p: 2,
          flexGrow: 1,
          height: 0,
          overflow: 'auto',
        }}
      >
        {poiCategories.length > 0 && (
          <MapSettings
            includePoiMap={includePoiMap}
            poiMapZoomLevel={poiMapZoomLevel}
            onIncludePoiMapChange={onIncludePoiMapChange}
            onPoiMapZoomLevelChange={onPoiMapZoomLevelChange}
          />
        )}

        <Stack
          direction="row"
          spacing={2}
          sx={{
            mt: 2,
          }}
        >
          <LoadingButton
            startIcon={<SparklesIcon />}
            loadingPosition="start"
            fullWidth
            loading={autoPoisLoading}
            onClick={() => {
              if (pois.length > 0) {
                setConfirmDialogOpen(true);
              } else {
                handleAutoSearch();
              }
            }}
            variant="contained"
          >
            {t('Automatically')}
          </LoadingButton>
          <Button
            startIcon={<MousePointerClickIcon />}
            fullWidth
            variant="outlined"
            onClick={() => setManualSearchOpen(true)}
          >
            {t('Manually')}
          </Button>
        </Stack>

        <Box>
          {poiCategories.map(category => {
            const categoryPois = pois.filter(poi =>
              category.primaryTypes.includes(poi.category),
            );

            if (categoryPois.length === 0) {
              return null;
            }

            categoryPois.sort((a, b) => a.distance - b.distance);

            return (
              <Box
                key={category.name}
                sx={{
                  mb: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    bgcolor: cssTokens.actionSubtle,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ mr: 1 }}>{category.icon}</Box>
                  <Typography variant="subtitle1">{category.name}</Typography>
                </Box>

                <List dense disablePadding>
                  {categoryPois.map(poi => (
                    <POIListItem
                      key={poi.id}
                      poi={poi}
                      primaryType={getPoiTypeLabel(poi.primaryType)}
                      updateReport={updateReport}
                    />
                  ))}
                </List>
              </Box>
            );
          })}
        </Box>

        {/* Auto-search Confirmation Dialog */}
        <ConfirmationDialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          title={t('Refresh Points of Interest')}
          onConfirm={() => {
            setConfirmDialogOpen(false);
            handleAutoSearch();
          }}
          confirmText={t('Proceed')}
          message={t(
            'This will replace your current list of points of interest with a new list. Are you sure you want to proceed?',
          )}
        />

        {/* Manual Search Dialog */}
        <SlideDialog
          open={manualSearchOpen}
          onClose={() => {
            setManualSearchOpen(false);
          }}
          title={t('Search for Points of Interest')}
          maxWidth="sm"
          fullWidth
          footerActions={null}
        >
          <Box
            sx={{
              p: 2,
            }}
          >
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('Search for specific points of interest near the property')}
            </Typography>
            <Autocomplete
              onChange={(_, value) => {
                if (value) {
                  handlePlaceSelect(value);
                }
              }}
              onInputChange={handleSearchChange}
              options={manualPoiOptions}
              getOptionLabel={option => option.name}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              loading={searchLoading}
              filterOptions={x => x}
              renderInput={params => (
                <TextField
                  {...params}
                  autoFocus
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{ background: 'var(--mui-palette-background-paper)' }}
                  slotProps={{
                    ...params.slotProps,

                    input: {
                      ...params.slotProps.input,
                      endAdornment: (
                        <>
                          {searchLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.slotProps.input.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
              renderOption={(props, option) => {
                const category = poiCategories.find(cat =>
                  cat.primaryTypes.includes(option.category),
                );
                const translatedCategoryName = category
                  ? category.name
                  : option.category;
                return (
                  <Box component="li" {...props} key={option.id}>
                    <Stack spacing={0.5}>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {option.formattedAddress} •{' '}
                        {formatDistance(option.distance)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          opacity: 0.7,
                        }}
                      >
                        {getPoiTypeLabel(option.primaryType)} (
                        {translatedCategoryName})
                      </Typography>
                    </Stack>
                  </Box>
                );
              }}
            />
          </Box>
        </SlideDialog>

        {apolloError && (
          <RASnackbar
            onClose={() => setErrorLike(null)}
            message={apolloError?.message}
          />
        )}
      </Stack>
      <FooterActionsComponentWithoutForm updating={false} {...props} />
    </>
  );
};

export default StepPointsOfInterest;
